import { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { useEntity, GROUP_VALUE } from "@/lib/entityStore";
import { Card } from "@/components/ds/Card";
import { PageShell, PageHeader, Section, Loading } from "@/components/ds/PageShell";
import { Stat } from "@/components/ds/Stat";
import { SeverityDot } from "@/components/ds/Severity";
import {
    LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

export default function Cashflow() {
    const { selectedEntityId } = useEntity();
    const [data, setData] = useState(null);
    const [reality, setReality] = useState(null);

    useEffect(() => {
        const q = selectedEntityId === GROUP_VALUE ? "" : `?entity_id=${selectedEntityId}`;
        api.get(`/cashflow${q}`).then((r) => setData(r.data));
        api.get(`/cash-reality${q}`).then((r) => setReality(r.data));
    }, [selectedEntityId]);

    if (!data)
        return (
            <PageShell>
                <Loading label="Loading cashflow…" testId="cashflow-loading" />
            </PageShell>
        );

    if (data.scope === "group") {
        const combined = {};
        for (const p of data.per_entity) {
            for (const point of p.cash_series_30d) {
                combined[point.date] = (combined[point.date] || 0) + point.net;
            }
        }
        const groupSeries = Object.entries(combined)
            .map(([date, net]) => ({ date, net }))
            .sort((a, b) => a.date.localeCompare(b.date));
        const total30 = groupSeries.reduce((a, b) => a + b.net, 0);

        return (
            <PageShell width="default" testId="cashflow-group">
                <PageHeader
                    eyebrow="Cashflow — Group"
                    title="Group cashflow"
                    subtitle="Daily net cash movement aggregated across all six entities. Negative days mean the group spent more than it collected."
                />

                <Card variant="default" padding="lg" className="mb-6" testId="cashflow-summary">
                    <div className="label-soft mb-2 text-stone-500">30-day pulse</div>
                    <div className="text-display-md text-stone-900 leading-snug mb-2">
                        {total30 >= 0 ? (
                            <>Group net cashflow positive by <span className="text-emerald-700">{fmtMoney(total30)}</span>.</>
                        ) : (
                            <>Group consumed <span className="text-red-700">{fmtMoney(Math.abs(total30))}</span> of net cash.</>
                        )}
                    </div>
                    <p className="text-body text-stone-500 max-w-prose">
                        {total30 >= 0
                            ? "Cash in is keeping up with cash out at the consolidated level."
                            : `If this rate persists, the group burns ~${Math.round((Math.abs(total30) / Math.max(1, data.total_cash)) * 100)}% of cash on hand each month.`}
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-5 mt-6">
                        <Stat label="Cash on hand" value={data.total_cash} size="sm" testId="kpi-cf-cash" />
                        <Stat label="Net 30-day" value={total30} size="sm" tone={total30 >= 0 ? "positive" : "negative"} testId="kpi-cf-net30" />
                        <Stat label="Entities" value={data.per_entity.length} money={false} size="sm" testId="kpi-cf-entities" />
                    </div>
                </Card>

                <Section title="Last 30 days" subtitle="Daily net cash across the group.">
                    <Card variant="default" padding="lg">
                        <div style={{ width: "100%", height: 280, minHeight: 280 }}>
                            <ResponsiveContainer>
                                <LineChart data={groupSeries}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                                    <XAxis dataKey="date" stroke="#78716c" fontSize={10} tickFormatter={(d) => d.slice(5)} />
                                    <YAxis stroke="#78716c" fontSize={10} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                                    <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ borderRadius: 6, fontSize: 12 }} />
                                    <Line type="step" dataKey="net" stroke="#a12a65" dot={false} strokeWidth={1.5} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>
                </Section>

                <Section title="Per-entity 30-day">
                    <Card variant="default" padding="none" className="overflow-hidden">
                        <table className="w-full text-[14px]">
                            <thead>
                                <tr className="text-left border-b border-stone-100 bg-stone-50/40">
                                    <th className="px-5 py-3 label-soft text-stone-500 font-medium">Entity</th>
                                    <th className="px-3 py-3 label-soft text-stone-500 font-medium text-right">Cash on hand</th>
                                    <th className="px-3 py-3 label-soft text-stone-500 font-medium text-right">Net 30-day</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.per_entity.map((p) => {
                                    const net = p.cash_series_30d.reduce((a, b) => a + b.net, 0);
                                    return (
                                        <tr key={p.entity_id} className="border-b border-stone-100 last:border-0">
                                            <td className="px-5 py-3.5 text-stone-800">{p.display_name}</td>
                                            <td className="px-3 py-3.5 font-mono-num text-right text-stone-700">{fmtMoney(p.cash_position)}</td>
                                            <td className={`px-3 py-3.5 font-mono-num text-right ${net >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                                                {fmtMoney(net)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </Card>
                </Section>

                {reality && <CashRealityPanel reality={reality} isGroup={true} />}
            </PageShell>
        );
    }

    const total30 = data.cash_series_30d.reduce((a, b) => a + b.net, 0);
    return (
        <PageShell width="default" testId="cashflow-entity">
            <PageHeader
                eyebrow="Cashflow — Entity"
                title="Entity cashflow"
                subtitle="Daily net cash movement for the selected entity."
            />

            <Card variant="default" padding="lg" className="mb-6">
                <div className="grid grid-cols-2 gap-x-8 gap-y-5">
                    <Stat label="Cash on hand" value={data.cash_position} size="md" testId="kpi-cf-cash" />
                    <Stat label="Net 30-day cashflow" value={total30} size="md" tone={total30 >= 0 ? "positive" : "negative"} testId="kpi-cf-net30" />
                </div>
            </Card>

            <Section title="Last 30 days">
                <Card variant="default" padding="lg">
                    <div style={{ width: "100%", height: 280 }}>
                        <ResponsiveContainer>
                            <LineChart data={data.cash_series_30d}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                                <XAxis dataKey="date" stroke="#78716c" fontSize={10} tickFormatter={(d) => d.slice(5)} />
                                <YAxis stroke="#78716c" fontSize={10} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                                <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ borderRadius: 6, fontSize: 12 }} />
                                <Line type="step" dataKey="cash_in" stroke="#44403c" dot={false} strokeWidth={1.5} />
                                <Line type="step" dataKey="cash_out" stroke="#a12a65" dot={false} strokeWidth={1.5} />
                                <Line type="step" dataKey="net" stroke="#10b981" dot={false} strokeWidth={1.5} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
            </Section>

            {reality && <CashRealityPanel reality={reality} isGroup={false} />}
        </PageShell>
    );
}

function CashRealityPanel({ reality, isGroup }) {
    const t = isGroup ? reality.totals : reality;
    const earliest = isGroup ? reality.earliest_group_shortfall_window_days : reality.shortfall_window_days;
    const shortfallLevel = earliest && earliest <= 30 ? "high" : earliest && earliest <= 60 ? "medium" : "low";

    return (
        <Section
            title="Cash reality"
            subtitle="What's actually available, after committed obligations."
            action={
                earliest ? (
                    <div className="flex items-center gap-2 text-[12px] text-stone-600">
                        <SeverityDot level={shortfallLevel} />
                        <span>Shortfall window {earliest}d</span>
                    </div>
                ) : null
            }
        >
            <Card variant="default" padding="lg" testId="cash-reality-panel">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-8 gap-y-5">
                    <Stat label="Real cash now" value={t.real_cash_now} size="sm" />
                    <Stat label="Expected AR 30d" value={t.expected_receivables_30d} size="sm" />
                    <Stat label="Committed AP 30d" value={t.committed_payables_30d} size="sm" />
                    <Stat label="Payroll 30d" value={t.payroll_pressure_30d} size="sm" />
                    <Stat label="Projected cash 30d" value={t.projected_cash_30d} size="sm" tone={t.projected_cash_30d >= 0 ? "default" : "negative"} />
                    <Stat label="Projected cash 90d" value={t.projected_cash_90d} size="sm" tone={t.projected_cash_90d >= 0 ? "default" : "negative"} />
                </div>
                {reality.entity_to_watch && (
                    <div className="mt-5 pt-4 border-t border-stone-100 text-[13px] text-stone-600">
                        <span className="text-stone-700 font-medium">Entity to watch:</span>{" "}
                        {reality.entity_to_watch.display_name} (shortfall in {reality.entity_to_watch.shortfall_window_days} days)
                    </div>
                )}
                {!isGroup && reality.cash_runway_days && (
                    <div className="mt-3 text-[13px] text-stone-600">
                        <span className="text-stone-700 font-medium">Cash runway:</span>{" "}
                        {reality.cash_runway_days} days at current burn rate
                    </div>
                )}
            </Card>
        </Section>
    );
}
